/**
 * Yande.re's strip: the best posts of the day, week, month or year.
 *
 * Yande.re has its own Popular page, but it is a separate list that takes no tags and does not page. The same result comes from a
 * score order inside a date window in an ordinary search (`order:score date:2026-10-18..`), which keeps tags and paging working.
 * Checked live against yande.re on 2026-10-25. Sent as its own parameter, not mixed into `tags`: `tags` is shared by every source in a
 * multi-source search and these terms mean nothing to the others.
 */
export const YANDERE_ORDERS = [
  { value: "day", days: 1, label: "Best of today" },
  { value: "week", days: 7, label: "Best of the week" },
  { value: "month", days: 30, label: "Best of the month" },
  { value: "year", days: 365, label: "Best of the year" },
] as const

export type YandereOrder = (typeof YANDERE_ORDERS)[number]["value"]

/** Yande.re turns a search away above 6 tags; the strip adds two search terms. */
const SITE_TAG_LIMIT = 6
const TERMS_ADDED = 2

export function isYandereOrder(value: unknown): value is YandereOrder {
  return typeof value === "string" && YANDERE_ORDERS.some((item) => item.value === value)
}

function dayString(daysAgo: number, now: Date): string {
  return new Date(now.getTime() - daysAgo * 86_400_000).toISOString().slice(0, 10)
}

/** `tags` for one source: unchanged for every source but Yande.re (and when no order is chosen). A search that already has its own
 * order or date term keeps it, and so does one with too many tags for the two extra terms to fit under the site's limit. */
export function withYandereOrder(site: string, tags: string, order: unknown, now: Date = new Date()): string {
  if (site !== "yandere" || !isYandereOrder(order)) return tags
  if (/(^|[\s,])(order|date):/i.test(tags)) return tags
  const existing = tags.split(/[\s,]+/).filter(Boolean)
  if (existing.length + TERMS_ADDED > SITE_TAG_LIMIT) return tags
  const days = YANDERE_ORDERS.find((item) => item.value === order)!.days
  return [...existing, "order:score", `date:${dayString(days, now)}..`].join(" ")
}
